import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LegalFooter from "../components/LegalFooter";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function SignIn() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email, password);
      toast.success("Welcome back");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="signin-screen">
      <header className="px-6 py-5">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="signin-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-6 pt-6 pb-16">
        <div className="w-full max-w-md">
          <span className="brand-wordmark text-xs text-muted-foreground">FINALLOOK</span>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-3 mb-2">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-8">Welcome back. Continue where you left off.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="signin-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11"
                data-testid="signin-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary font-medium hover:underline"
                  data-testid="signin-forgot-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="h-11"
                data-testid="signin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              data-testid="signin-submit-btn"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            New to FINALLOOK?{" "}
            <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="signin-to-signup-link">
              Create an account
            </Link>
          </p>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
